import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { X, Loader2, Heart, Plus } from 'lucide-react';
import { BACKEND_URL } from '../../utils/config';

// Updated emojis with healing/support focus
const EMOJIS = [
  { id: "healing", emoji: "❤️‍🩹", label: "Healing" },
  { id: "flower", emoji: "💐", label: "Support" },
  { id: "pray", emoji: "🙏", label: "Prayers" },
  { id: "green_heart", emoji: "💚", label: "Hope" },
  { id: "clap", emoji: "👏", label: "Applause" },
  { id: "sparkle", emoji: "✨", label: "Amazing" },
  { id: "muscle", emoji: "💪", label: "Strength" },
  { id: "hug", emoji: "🤗", label: "Hugs" },
];

const EmojiReactions = ({ campaignId, position = "overlay" }) => {
  const { user, isAuthenticated } = useAuth();
  const [reactions, setReactions] = useState({
    counts: {},
    userReactions: [],
  });
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  useEffect(() => {
    fetchReactions();
  }, [campaignId, user]);

  const fetchReactions = async () => {
    try {
      const url = user 
        ? `${BACKEND_URL}/api/reactions/campaign/${campaignId}?user_id=${user.id}`
        : `${BACKEND_URL}/api/reactions/campaign/${campaignId}`;
      
      const res = await fetch(url);
      const data = await res.json();
      
      setReactions({
        counts: data.counts || {},
        userReactions: data.user_reactions || [],
      });
    } catch (err) {
      console.error('Error fetching reactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReaction = async (emojiId) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    setToggling(emojiId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/reactions/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign_id: campaignId,
          user_id: user.id,
          emoji_id: emojiId
        })
      });

      const data = await res.json();
      if (data.success) {
        setReactions(prev => ({
          ...prev,
          counts: data.counts,
          // User can only have ONE reaction - use the response directly
          userReactions: data.user_reactions || []
        }));
      }
    } catch (err) {
      console.error('Error toggling reaction:', err);
    } finally {
      setToggling(null);
    }
  };

  const getTotalReactions = () => {
    return Object.values(reactions.counts).reduce((sum, count) => sum + count, 0);
  };

  // Get top emojis that have been used
  const getActiveEmojis = () => {
    return EMOJIS
      .filter(e => (reactions.counts[e.id] || 0) > 0)
      .sort((a, b) => (reactions.counts[b.id] || 0) - (reactions.counts[a.id] || 0))
      .slice(0, 4);
  };

  if (loading) {
    return null;
  }

  const totalCount = getTotalReactions();
  const activeEmojis = getActiveEmojis();

  return (
    <>
      {/* Main React Button - Overlay style for campaign photo */}
      <div className="relative inline-block">
        <button
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          className="flex items-center gap-2 px-4 py-2 bg-black/70 backdrop-blur-sm rounded-full border border-white/20 hover:bg-black/80 transition-all group"
        >
          {/* Heart icon with plus */}
          <div className="relative">
            <Heart className="w-5 h-5 text-red-500 fill-red-500" />
            <Plus className="absolute -bottom-1 -right-1 w-3 h-3 text-white bg-emerald-500 rounded-full p-0.5" />
          </div>
          
          <span className="text-white text-sm font-medium">React</span>
          
          {/* Active emojis preview */}
          {activeEmojis.length > 0 && (
            <div className="flex -space-x-1">
              {activeEmojis.map((emoji) => (
                <span key={emoji.id} className="text-sm">{emoji.emoji}</span>
              ))}
            </div>
          )}
          
          {/* Count */}
          {totalCount > 0 && (
            <span className="text-white/90 text-sm font-semibold underline decoration-dotted">
              {totalCount}
            </span>
          )}
        </button>

        {/* Emoji Picker Popup */}
        {showEmojiPicker && (
          <div className="absolute bottom-full right-0 mb-2 p-3 bg-card border border-border rounded-2xl shadow-2xl z-[100] w-[280px]">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-foreground">React to this campaign</span>
              <button 
                onClick={() => setShowEmojiPicker(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="grid grid-cols-4 gap-1.5">
              {EMOJIS.map((emoji) => {
                const count = reactions.counts[emoji.id] || 0;
                const isSelected = reactions.userReactions.includes(emoji.id);
                
                return (
                  <button
                    key={emoji.id}
                    onClick={() => handleReaction(emoji.id)}
                    disabled={toggling === emoji.id}
                    className={`flex flex-col items-center p-2 rounded-xl transition-all hover:scale-105 ${
                      isSelected
                        ? 'bg-emerald-500/20 ring-2 ring-emerald-500'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                    title={emoji.label}
                  >
                    {toggling === emoji.id ? (
                      <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
                    ) : (
                      <span className="text-xl">{emoji.emoji}</span>
                    )}
                    <span className="text-[10px] text-muted-foreground mt-0.5">
                      {count > 0 ? count : ''}
                    </span>
                  </button>
                );
              })}
            </div>
            
            {!isAuthenticated && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground text-center">
                  Sign in to react to this campaign
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowAuthModal(false)}>
          <div className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🙌</span>
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Join the Community!</h3>
              <p className="text-muted-foreground text-sm mb-6">
                Sign in to react to campaigns and show your support.
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => window.location.href = '/auth'}
                  className="w-full bg-emerald-500 hover:bg-emerald-600"
                >
                  Sign In / Sign Up
                </Button>
                <Button 
                  onClick={() => setShowAuthModal(false)}
                  variant="ghost"
                  className="w-full"
                >
                  Maybe Later
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmojiReactions;
